import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import CustomerRegistrationForm from '../components/customers/CustomerRegistrationForm';
import LoadingScreen from '../components/common/LoadingScreen';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { LanguageProvider } from '../contexts/LanguageContext';
import { ThemeProvider } from '../contexts/ThemeContext';

interface Organization {
  id: string;
  name: string;
  logo?: string;
  primaryColor?: string;
}

const PublicCustomerRegistration: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!organizationId) {
        setError('Érvénytelen regisztrációs link');
        setLoading(false);
        return;
      }

      try {
        const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
        
        if (orgDoc.exists()) {
          setOrganization({
            id: orgDoc.id,
            ...orgDoc.data()
          } as Organization);
        } else {
          setError('A szervezet nem található');
        }
      } catch (error) {
        console.error('Error fetching organization:', error);
        setError('Hiba történt az adatok betöltése során');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [organizationId]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !organization) {
    return (
      <ThemeProvider>
        <LanguageProvider>
          <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center border border-gray-200 dark:border-gray-700">
              <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Hiba történt
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {error || 'Ismeretlen hiba történt'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Kérjük, ellenőrizze a regisztrációs linket, vagy vegye fel a kapcsolatot velünk.
              </p>
            </div>
          </div>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <CustomerRegistrationForm
          organizationId={organizationId!}
          onSuccess={() => {
            // Opcionális: redirect vagy további műveletek
          }}
        />
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default PublicCustomerRegistration;
